<template>
  <div>
    <qr-address-update v-model="showEditDialog" @reload="valueModel = $event" />

    <v-text-field
      v-model="valueModel"
      :clearable="!isReadOnly"
      :loading="loading"
      :placeholder="placeholder"
      :rules="[dtUrlRule]"
      class="identifier-input"
      clear-icon="mdi-close-circle-outline"
      direction="ltr"
      style="direction: ltr"
      v-bind="$attrs"
      @click="isReadOnly && (showEditDialog = true)"
      persistent-hint
      :hint="$t('qrAddressUpdate.hints.allowedChars')"
      :key="$t('qrAddressUpdate.hints.allowedChars')"
    >
      <template v-slot:prepend-inner>
        <v-card
          class="d-flex align-center px-2"
          color="grey lighten-2"
          flat
          height="40"
          width="fit-content"
        >
          <v-menu
            v-model="menu"
            :disabled="$attrs.disabled || isReadOnly || isMenuReadOnly"
            bottom
            nudge-bottom="5"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                :ripple="false"
                class="pa-0"
                style="text-transform: unset"
                text
                v-bind="attrs"
                v-on="on"
              >
                <v-fade-transition mode="out-in">
                  <v-icon v-if="!menu" key="down" class="ms-1"
                    >mdi-chevron-down</v-icon
                  >
                  <v-icon v-else key="up" class="ms-1">mdi-chevron-up</v-icon>
                </v-fade-transition>
                <span
                  v-if="
                    destItems && destItems.length > 0 && destItems[destModel]
                  "
                  >{{ destItems[destModel].name }}</span
                >
                <v-progress-circular
                  v-else-if="loading"
                  color="primary"
                  indeterminate
                  size="20"
                  width="3"
                />
              </v-btn>
            </template>

            <v-list
              v-if="destItems && destItems.length > 0"
              :disabled="loading"
              dense
              min-width="150"
            >
              <v-list-item-group v-model="destModel" style="perspective: 500px">
                <template v-for="(item, i) in destItems">
                  <v-list-item
                    v-if="item.user_id"
                    :id="`did-item-${item.id}`"
                    :key="i"
                    :class="{ 'active-rotate': item.rotate }"
                    :disabled="item.disabled"
                    class="rotate-item"
                    color="primary"
                  >
                    <div class="d-flex flex front-rotate">
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                      <v-list-item-action>
                        <v-icon @click.stop="deleteItem(item)"
                          >mdi-delete</v-icon
                        >
                      </v-list-item-action>
                    </div>

                    <div class="d-flex flex back-rotate">
                      <v-list-item-title>{{ $t("delete") }}</v-list-item-title>
                      <v-list-item-action
                        class="flex-nowrap flex-row justify-center align-self-center my-1"
                      >
                        <v-icon
                          color="white"
                          right
                          @click.stop="destroy(item.id)"
                          >mdi-delete</v-icon
                        >
                        <v-icon
                          color="white"
                          right
                          @click.stop="deleteItem(item)"
                          >mdi-close-circle-outline</v-icon
                        >
                      </v-list-item-action>
                    </div>
                  </v-list-item>

                  <v-list-item
                    v-else
                    :key="i"
                    :disabled="item.disabled"
                    color="primary"
                  >
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                  </v-list-item>
                </template>

                <v-list-item class="mt-2">
                  <v-tooltip color="success" right>
                    <template v-slot:activator="{ on, attr }">
                      <v-btn
                        block
                        color="success"
                        outlined
                        v-bind="attr"
                        @click="showAddDialog = true"
                        v-on="on"
                      >
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </template>

                    <span>{{ $t("personal_domain") }}</span>
                  </v-tooltip>
                </v-list-item>
              </v-list-item-group>
            </v-list>

            <v-card v-else class="pa-3"> {{ $t("no_domain_found") }}</v-card>
          </v-menu>
        </v-card>
      </template>
    </v-text-field>
    <v-expand-transition>
      <div
        v-if="hasInvalidChars"
        class="d-flex justify-end mb-2 pr-2"
        style="margin-top: -10px"
      >
        <v-btn
          class="rounded-lg btn-edit ma-2"
          x-small
          outlined
          rounded
          @click="cleanInvalidChars"
        >
          <v-icon left small>mdi-auto-fix</v-icon>
          حذف کاراکترهای غیرمجاز
        </v-btn>
      </div>
    </v-expand-transition>
    <custom-popup v-model="showAddDialog" :cons="cons" max-width="500">
      <v-card flat>
        <v-card-title class="text-break">
          {{ $t("enter_domain_address") }}
        </v-card-title>
        <v-card-subtitle>
          {{ $t("dns_setup_note") }}
        </v-card-subtitle>

        <v-card-text class="font-size-h6">
          <v-text-field
            v-model="customDomain"
            :error-messages="errors.name"
            :loading="loading"
            :rules="urlRules"
            append-icon="mdi-web"
            clear-icon="mdi-close-circle-outline"
            clearable
            dense
            outlined
            @keydown.enter="addNewDomain"
          />

          <b>{{ $t("attention") }}</b>
          {{ $t("dns_setup_instruction") }}
          <v-tooltip v-model="showTooltip[0].status" color="green darken-1" top>
            <template v-slot:activator="{ on }">
              <b
                class="primary--text cursor-pointer"
                @click="copyTxt('ns1.asiatech.name', 0)"
                >ns1.asiatech.name</b
              >
            </template>

            <span>{{ $t("copied") }}</span>
          </v-tooltip>
          {{ $t("and") }}
          <v-tooltip v-model="showTooltip[1].status" color="green darken-1" top>
            <template v-slot:activator="{ on }">
              <b
                class="primary--text cursor-pointer"
                @click="copyTxt('ns2.asiatech.name', 1)"
                >ns2.asiatech.name</b
              >
            </template>

            <span>{{ $t("copied") }}</span>
          </v-tooltip>
          {{ $t("setup_on_domain") }}
          <br />
          <br />
          {{ $t("dns_change_time") }}
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn
            :disabled="!allowToAdd"
            :loading="loading"
            color="success"
            depressed
            @click="addNewDomain"
          >
            <v-icon left>mdi-plus</v-icon>
            {{ $t("add_domain") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </custom-popup>
  </div>
</template>

<script>
import QrAddressUpdate from "@/view/content/fragments/qrAddressUpdate.vue";

export default {
  name: "qrAddressField",

  components: { QrAddressUpdate },

  props: {
    value: [String, null],
    domainId: Number,
    filterDomainId: {
      type: [Number, null],
      default: 5,
    },
  },

  data() {
    return {
      menu: false,
      destModel: 0,
      destItems: [],
      showAddDialog: false,
      customDomain: "",
      cons: {
        title: this.$t("add_personal_domain"),
        buttons: [
          {
            type: "submit",
            handler: this.addNewDomain,
            disabled: () => !this.allowToAdd,
          },
        ],
      },
      loading: false,
      errors: {},
      placeholder: "terminalads",
      dtUrlRule: (v) => {
        if (!v || v.length < 3) {
          return this.$t("min_characters") || "حداقل ۳ کاراکتر الزامی است";
        }

        const isValid = /^[a-zA-Z0-9\-_]+$/.test(v);
        if (!isValid) {
          return this.$t('qrAddressUpdate.errors.invalidChars');
        }

        return true;
      },
      urlRules: [(value) => this.isDomain(value) || this.$t("domain_format")],
      showTooltip: [
        {
          status: false,
        },
        {
          status: false,
        },
      ],
      showEditDialog: false,
    };
  },

  mounted() {
    this.fetch();
  },

  computed: {
    hasInvalidChars() {
      if (!this.valueModel) return false;
      return /[^a-zA-Z0-9\-_]/.test(this.valueModel);
    },

    valueModel: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    allowToAdd() {
      return (
        this.customDomain &&
        this.customDomain.length > 0 &&
        this.isDomain(this.customDomain)
      );
    },
    isReadOnly() {
      return this.$attrs.hasOwnProperty("readonly");
    },
    isMenuReadOnly() {
      return this.$attrs.hasOwnProperty("menureadonly");
    },
  },

  watch: {
    destModel(val) {
      this.$emit("updateDest", this.destItems[val]?.id);
    },
    domainId(val) {
      this.destModel = this.destItems.findIndex((item) => item.id === val);
    },
    // destItems() {
    //   if (this.filterDomainId) {
    //     this.destItems = this.destItems.filter(
    //       (item) => item.id !== this.filterDomainId
    //     );
    //   }
    // },
    menu(val) {
      if (!val) {
        this.$nextTick(() => {
          this.destItems.forEach((item) => {
            if (item.user_id && item.rotate) this.deleteItem(item);
          });
        });
      }
    },
    showTooltip: {
      deep: true,

      handler(val) {
        if (val) {
          setTimeout(() => {
            this.showTooltip = [
              {
                status: false,
              },
              {
                status: false,
              },
            ];
          }, 2000);
        }
      },
    },
  },

  methods: {
    cleanInvalidChars() {
      if (this.valueModel) {
        this.valueModel = this.valueModel.replace(/[^a-zA-Z0-9\-_]/g, "");
      }
    },
    fetch() {
      this.loading = true;

      this.$api
        .get("/v1/domain")
        .then(({ data }) => {
          this.destItems = data.data;
          this.$nextTick(() => {
            if (data.data && data.data.length > 0)
              if (this.domainId) {
                this.destModel = data.data.findIndex(
                  (item) => item.id === this.domainId,
                );
              } else {
                this.$emit("updateDest", this.destItems[0].id);
              }
            // this.computedDest = this.destItems[this.destModel]?.id
          });
        })
        .catch(({ response }) =>
          console.log("error in get domains: ", response),
        )
        .finally(() => (this.loading = false));
    },
    isURL(str) {
      let regex = new RegExp("^[a-zA-Z0-9\\-_~:?#[\\]@!$&'()+,;=.]+$");
      return regex.test(str);
    },
    isDomain(str) {
      var regex = new RegExp(
        "^(http[s]?:\\/\\/(www\\.)?|ftp:\\/\\/(www\\.)?|www\\.){1}([0-9A-Za-z-\\.@:%_\+~#=]+)+((\\.[a-zA-Z]{2,3})+)(/(.)*)?(\\?(.)*)?",
      );
      var without_regex = new RegExp(
        "^([0-9A-Za-z-\\.@:%_\+~#=]+)+((\\.[a-zA-Z]{2,3})+)(/(.)*)?(\\?(.)*)?",
      );
      if (
        !str ||
        regex.test(str) ||
        without_regex.test(str) ||
        str.length <= 0
      ) {
        return true;
      } else {
        return false;
      }
    },
    addNewDomain() {
      if (!this.allowToAdd) return this.$toast.error(this.$t("invalid_domain"));

      this.loading = true;
      this.errors = {};

      this.$api
        .post("/v1/domain/store", {
          name: this.customDomain,
        })
        .then(({ data }) => {
          this.$toast.success(this.$t("domain_added_successfully"));
          this.showAddDialog = false;
          this.fetch();
        })
        .catch(({ response }) => {
          this.showAddDialog = false;
          if (response.data?.errors) {
            if (response.data.errors.status) {
              this.$toast.error(this.$t("invalid_dns_settings"));
              return;
            } else this.errors = response.data.errors;
          }
          if (response.data?.message) {
            this.$toast.error(response.data.message);
          }
        })
        .finally(() => (this.loading = false));
    },
    deleteItem(item) {
      this.$nextTick(() => {
        let d = document.getElementById(`did-item-${item.id}`);
        if (!item.rotate) {
          item.rotate = true;
          d.classList.add("active-rotate");
        } else {
          item.rotate = false;
          if (d.classList.contains("active-rotate")) {
            d.classList.remove("active-rotate");
          }
        }
      });
    },
    destroy(id) {
      this.loading = true;

      this.$api
        .delete(`/v1/domain/delete/${id}`)
        .then(({ data }) => {
          this.$toast.success(this.$t("deleted_successfully"));
          this.fetch();
        })
        .catch(({ response }) =>
          console.log("error in delete domain: ", response),
        )
        .finally(() => (this.loading = false));
    },
    copyTxt(txt, index) {
      navigator.clipboard.writeText(txt);
      this.showTooltip[index].status = true;
    },
  },
};
</script>

<style scoped>
.v-input.identifier-input >>> .v-input__slot {
  padding-left: 0 !important;
}

.v-input >>> .v-input__prepend-inner {
  margin: 0 !important;
  padding-left: 0;
  padding-right: 4px;
}

.qr-address-field >>> .v-input__append-inner {
  padding: 0 0 0 4px;
}

.select-dest >>> .v-input__slot {
  padding: 0 !important;
}

.select-dest >>> .v-input__append-inner {
  padding: 0 !important;
  margin: 0 !important;
}

.select-dest >>> .v-select__selection {
  font-size: small !important;
}

.v-btn:before,
.v-btn:hover:before {
  opacity: 0 !important;
}

.rotate-item {
  transform-style: preserve-3d;
  transform: rotateX(0);
  transition: transform 0.3s cubic-bezier(0.217, 0.6, 0.355, 1);
}

.rotate-item.active-rotate {
  transform: rotateX(90deg);
}

.rotate-item:hover:before {
  opacity: 0 !important;
}

.rotate-item >>> .front-rotate,
.rotate-item >>> .back-rotate {
  backface-visibility: hidden;
  position: absolute;
  top: 0;
  left: 50%;
  width: 100%;
  height: 100%;
  display: block;
  padding: 0 16px;
}

.rotate-item >>> .front-rotate {
  transform: rotateX(0) translateX(-50%) translateZ(19px);
}

.rotate-item:hover >>> .front-rotate {
  background: rgba(0, 0, 0, 0.1);
}

.rotate-item >>> .back-rotate {
  background: var(--v-third-base);
  color: white !important;
  transform: rotateX(-90deg) translateX(-50%) translateZ(19px);
}
</style>
